<script setup lang="ts">
import { DevicePresenceStateDto, QaEquipmentCatalogDto } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import { success, warning } from '@/components/icons/icons';
import { useTranslation } from 'i18next-vue';
import { computed, onMounted, ref, watch } from 'vue';
import { DetectorCandidate, reportedArrayOf } from './detectorConfirmation';
import { useDetectorCandidates } from './useDetectorConfirmation';

/**
 * Asks the user to confirm that the array of the set is the one physically
 * connected to the detector interface. The interface reports only the type
 * of its array, not the serial number, so two arrays of one type cannot be
 * told apart electronically; only the label on the device can. Shows the
 * set's array (type, ID, REF, serial), what the interface reports (as
 * information, never compared), and the confirmation itself: a plain
 * checkbox when the array is the only enabled one of its model, or a pick
 * among the serial numbers of all enabled arrays of that model, so the user
 * has to read the label. A wrong pick names the array that serial belongs to
 * and does not confirm. When the fleet cannot be loaded the checkbox is
 * shown, so a failed lookup never stops a QA session. Confirmed, the box
 * folds shut while one line with a way back unfolds (Vuetify's expand
 * transition on each, as on the app's panels), so whatever follows below
 * slides once, straight to where it ends up; the way back runs the same in
 * reverse. A beat after the line is open, its check gives one big bounce,
 * the way the start button's check pops after a run.
 */
const props = defineProps<{
  /** The detector interface of the set. */
  detectorInterface: QaEquipmentCatalogDto;
  /** The array of the set, the one to confirm. */
  array: QaEquipmentCatalogDto;
  /** Live presence of the interface, for the reported line. */
  presence?: DevicePresenceStateDto;
  /** Name of the linac the set is used at. */
  targetSystemName?: string;
  confirmed: boolean;
  /** Blocks the way back out of the confirmed state, e.g. while a run is under way. */
  changeBlocked?: boolean;
}>();

const emits = defineEmits<{
  confirm: [];
  change: [];
}>();

const { t } = useTranslation();

const { candidates, loading, failed, load } = useDetectorCandidates(() => props.array);

// Loaded whenever the box is asking, not while it shows the confirmation:
// the fleet may have changed since.
onMounted(() => {
  if (!props.confirmed) {
    load();
  }
});
watch(
  () => props.confirmed,
  (confirmed) => {
    if (!confirmed) {
      load();
    }
  },
);

// The serial number is what the user reads on the label; the ID stands in
// for an array registered without one.
const serialNumber = computed(() => props.array.serialNumber || undefined);
const arrayIdentity = computed(() => serialNumber.value ?? props.array.iuid ?? '');

// Only a choice between several serial numbers forces reading the label.
// Without a serial number of its own the set's array cannot be picked.
const pickMode = computed(() => !!serialNumber.value && (candidates.value?.length ?? 0) > 1);

const checked = ref(false);
const selectedId = ref<string>();
const mismatch = ref<DetectorCandidate>();

watch(selectedId, () => {
  mismatch.value = undefined;
});
watch(
  () => props.confirmed,
  () => {
    checked.value = false;
    selectedId.value = undefined;
    mismatch.value = undefined;
  },
);

const canConfirm = computed(() => {
  if (loading.value) {
    return false;
  }
  return pickMode.value ? !!selectedId.value : checked.value;
});

function confirm() {
  if (!canConfirm.value) {
    return;
  }
  if (pickMode.value && selectedId.value !== props.array.id) {
    mismatch.value = candidates.value?.find((candidate) => candidate.id === selectedId.value);
    return;
  }
  emits('confirm');
}

const interfaceLabel = computed(() =>
  [props.detectorInterface.name, props.detectorInterface.iuid].filter(Boolean).join(' | '),
);

const reported = computed(() => reportedArrayOf(props.presence));
const reportedText = computed(() => {
  const values = { interface: interfaceLabel.value };
  switch (reported.value.state) {
    case 'connected':
      return t('DetectorConfirmationReportedConnected', {
        ...values,
        type: reported.value.typeName,
      });
    case 'noArray':
      return t('DetectorConfirmationReportedNoArray', values);
    default:
      return t('DetectorConfirmationReportedOffline', values);
  }
});

// The statement the checkbox confirms, naming the model and the identity.
const statement = computed(() =>
  t(
    props.targetSystemName
      ? 'DetectorConfirmationStatement'
      : 'DetectorConfirmationStatementNoTarget',
    {
      model: props.array.model,
      serial: arrayIdentity.value,
      target: props.targetSystemName ?? '',
    },
  ),
);

const fields = computed(() => [
  { label: t('Detector'), value: props.array.model, strong: true },
  { label: t('ID'), value: props.array.iuid ?? '–', strong: false },
  { label: t('REF'), value: props.array.typeNumber ?? '–', strong: false },
  { label: t('SerialNumber'), value: serialNumber.value ?? '–', strong: true },
]);
</script>

<template>
  <div>
    <v-expand-transition>
      <div
        v-if="confirmed"
        class="detector-confirmed d-flex align-center ga-2"
        data-testid="detector-confirmed"
      >
        <v-icon
          :icon="success"
          color="success"
          size="20"
          class="detector-confirmed__check"
          data-testid="detector-confirmed-check"
        />
        <span class="flex-grow-1">
          {{ t('DetectorConfirmationConfirmed', { model: array.model, serial: arrayIdentity }) }}
        </span>
        <Button
          variant="text"
          size="small"
          :label="t('DetectorConfirmationChange')"
          :disabled="changeBlocked"
          data-testid="detector-change"
          @click="emits('change')"
        />
      </div>
    </v-expand-transition>

    <!-- A plain wrapper folds: the alert's own padding would stop the fold short. -->
    <v-expand-transition>
      <div v-if="!confirmed">
        <v-alert
          type="warning"
          variant="tonal"
          :icon="warning"
          class="detector-confirmation"
          data-testid="detector-confirmation"
        >
          <template #title>
            {{ t('DetectorConfirmationTitle') }}
          </template>

          <p class="text-high-emphasis mb-3">{{ t('DetectorConfirmationInstruction') }}</p>

          <div class="detector-confirmation__fields mb-3">
            <div v-for="field in fields" :key="field.label" class="detector-confirmation__field">
              <div class="text-caption text-medium-emphasis">{{ field.label }}</div>
              <div :class="field.strong ? 'font-weight-bold' : undefined">{{ field.value }}</div>
            </div>
          </div>

          <p class="text-body-2 text-medium-emphasis mb-3" data-testid="detector-reported">
            {{ reportedText }}
          </p>

          <p
            v-if="failed"
            class="text-body-2 text-high-emphasis mb-3"
            data-testid="detector-lookup-failed"
          >
            {{ t('DetectorConfirmationLookupFailed') }}
          </p>

          <div
            class="detector-confirmation__confirm d-flex flex-wrap align-center ga-4 text-high-emphasis"
          >
            <div class="flex-grow-1">
              <v-progress-circular v-if="loading" indeterminate size="20" width="2" />

              <template v-else-if="pickMode">
                <p class="mb-1">
                  {{ t('DetectorConfirmationPickInstruction', { model: array.model }) }}
                </p>
                <v-radio-group v-model="selectedId" hideDetails density="compact">
                  <v-radio
                    v-for="candidate in candidates"
                    :key="candidate.id"
                    :value="candidate.id"
                    :label="`SN ${candidate.serialNumber}`"
                    :data-testid="`detector-candidate-${candidate.id}`"
                  />
                </v-radio-group>
              </template>

              <v-checkbox
                v-else
                v-model="checked"
                hideDetails
                density="compact"
                data-testid="detector-checkbox"
              >
                <template #label>
                  <span>{{ statement }}</span>
                </template>
              </v-checkbox>
            </div>

            <Button
              variant="primary"
              :label="t('DetectorConfirmationConfirm')"
              :disabled="!canConfirm"
              data-testid="detector-confirm"
              @click="confirm()"
            />
          </div>

          <v-alert
            v-if="mismatch"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
            data-testid="detector-mismatch"
          >
            {{
              t('DetectorConfirmationMismatch', {
                serial: mismatch.serialNumber,
                model: mismatch.model,
                name: mismatch.name,
              })
            }}
          </v-alert>
        </v-alert>
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped lang="scss">
// The check bounces a beat after the line has unfolded (Vuetify's expand
// takes 0.3 s; the pause makes it its own gesture): well past its size, a
// dip, a smaller rise, rest. Same spirit as the start button's check pop.
$check-delay: 0.55s;
$check-bounce: 0.6s;

.detector-confirmed__check {
  animation: detector-check-bounce $check-bounce ease-out $check-delay both;
}

@keyframes detector-check-bounce {
  0% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.7);
  }
  55% {
    transform: scale(0.85);
  }
  75% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .detector-confirmed__check {
    animation: none;
  }
}

.detector-confirmation__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: 8px 16px;
  padding: 8px 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
}
</style>
